import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Statistic,
  Switch,
  Tabs,
  Typography,
} from "antd"
import { useState } from "react"
import { siteWorkflowApi } from "../../utils/siteWorkflowApi"
import { workflowRequest } from "../../utils/workflowTransport"
import { useWorkflowResource } from "./useWorkflowResource"
import { WorkflowError, WorkflowPager } from "./WorkflowCommon"
import { InspectionPlanningPanel } from "./InspectionPlanning"
import { CorrectiveActionsPanel } from "./CorrectiveActions"
import { InspectionComparisonPanel } from "./InspectionComparison"
import { useWorkflowSession } from "./workflowSession"
export function SiteOperationsPage() {
  const session = useWorkflowSession()
  const [enterprise, setEnterprise] = useState(session.enterpriseUuid)
  const [page, setPage] = useState(1),
    [query, setQuery] = useState(""),
    [archived, setArchived] = useState(false)
  const [selected, setSelected] = useState<{ id: string; name: string }>()
  const enterprises = useWorkflowResource(
    session.isSuperadmin ? "workflow-enterprises" : "",
    (signal) =>
      workflowRequest<{ UUID: string; Name: string }[]>(
        "enterprises",
        signal,
        "GET",
        undefined,
        true,
      ),
  )
  const data = useWorkflowResource(
    enterprise ? `site-overview/${enterprise}/${query}/${archived}/${page}` : "",
    (signal) => siteWorkflowApi.overview(enterprise, query, archived, page, signal),
  )
  return (
    <main style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%", padding: 16 }}>
      <Typography.Title level={2}>Suivi des sites</Typography.Title>
      {session.isSuperadmin && (
        <Select
          aria-label="Entreprise"
          style={{ minWidth: 240, maxWidth: "100%" }}
          value={enterprise || undefined}
          showSearch
          optionFilterProp="label"
          onChange={(value) => {
            setEnterprise(value)
            setSelected(undefined)
            setPage(1)
          }}
          options={(enterprises.data ?? []).map((e) => ({ value: e.UUID, label: e.Name }))}
        />
      )}
      <WorkflowError error={enterprises.error} retry={enterprises.reload} />
      {selected ? (
        <>
          <Space wrap>
            <Button onClick={() => setSelected(undefined)}>Tous les sites</Button>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {selected.name}
            </Typography.Title>
          </Space>
          <Tabs
            key={`${enterprise}/${selected.id}`}
            items={[
              {
                key: "planning",
                label: "Planning",
                children: <InspectionPlanningPanel site={selected.id} enterprise={enterprise} />,
              },
              {
                key: "actions",
                label: "Suivi des actions",
                children: <CorrectiveActionsPanel site={selected.id} enterprise={enterprise} />,
              },
              {
                key: "comparisons",
                label: "Comparaisons",
                children: <InspectionComparisonPanel site={selected.id} />,
              },
            ]}
          />
        </>
      ) : (
        <>
          <Space wrap>
            <Input.Search
              placeholder="Rechercher un site"
              onSearch={(value) => {
                setQuery(value)
                setPage(1)
              }}
              allowClear
            />
            <span>
              Archivés{" "}
              <Switch
                checked={archived}
                onChange={(v) => {
                  setArchived(v)
                  setPage(1)
                }}
              />
            </span>
            <Button onClick={data.reload}>Actualiser</Button>
          </Space>
          <WorkflowError error={data.error} retry={data.reload} />
          {!data.loading && !data.data?.items.length && (
            <Alert
              type="info"
              message="Aucun site dans cette sélection. Les dossiers sont créés et alimentés depuis le Gestionnaire de Sites de l’application d’inspection."
            />
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))",
              gap: 16,
            }}
          >
            {data.data?.items.map((s) => (
              <Card
                key={s.physicalSiteUuid}
                title={
                  <Button
                    type="link"
                    style={{ whiteSpace: "normal", height: "auto", textAlign: "left" }}
                    onClick={() => setSelected({ id: s.physicalSiteUuid, name: s.name })}
                  >
                    {s.name}
                  </Button>
                }
              >
                <Space wrap>
                  <Statistic title="Actions ouvertes" value={s.openActions} />
                  <Statistic
                    title="Actions en retard"
                    value={s.overdueActions}
                    valueStyle={{ color: s.overdueActions ? "#cf1322" : undefined }}
                  />
                  <Statistic title="Visites à venir" value={s.upcomingInspections} />
                  <Statistic
                    title="Visites en retard"
                    value={s.overdueInspections}
                    valueStyle={{ color: s.overdueInspections ? "#cf1322" : undefined }}
                  />
                </Space>
              </Card>
            ))}
          </div>
          <WorkflowPager page={page} total={data.data?.total} onChange={setPage} />
        </>
      )}
    </main>
  )
}
